// Optional day/night mode, and a 2D-first one: the always-visible part is a
// real solar terminator — the night hemisphere shaded on the map with a
// twilight-graded band, sweeping in real time, readable top-down at any zoom.
// It does NOT touch the 2D/3D state. The sun-lit shaded relief (a hillshade
// over the DEM) embosses the whole map and reads as 3D terrain, so it's tied to
// the actual 3D-terrain toggle (⛰): shown only when terrain is on, hidden in
// 2D. The atmospheric skybox is likewise only visible when the camera is
// pitched or on the globe.
//
// Toggled from the top strip. The terminator is global (time only); the relief
// and sky track the map centre. Everything refreshes as the map moves and once
// a minute while it's on.

import * as maplibregl from 'maplibre-gl';
import { installTerrain, TERRAIN_SOURCE_ID } from './basemaps';

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

// --- sun position at a point (a compact port of SunCalc's getPosition) -------

const DAY_MS = 86_400_000;
const J1970 = 2_440_588;
const J2000 = 2_451_545;
const OBLIQUITY = D2R * 23.4397; // axial tilt of the Earth

function toDays(date: Date): number {
  return date.valueOf() / DAY_MS - 0.5 + J1970 - J2000;
}

export interface SunPos {
  azimuth: number;  // compass degrees, from due north, clockwise
  altitude: number; // degrees above the horizon (negative = below)
}

export function sunPosition(date: Date, lat: number, lon: number): SunPos {
  const lw = D2R * -lon;
  const phi = D2R * lat;
  const d = toDays(date);
  const m = D2R * (357.5291 + 0.98560028 * d);                 // mean anomaly
  const c = D2R * (1.9148 * Math.sin(m) + 0.02 * Math.sin(2 * m)
                   + 0.0003 * Math.sin(3 * m));                // equation of centre
  const l = m + c + D2R * 102.9372 + Math.PI;                  // ecliptic longitude
  const dec = Math.asin(Math.sin(OBLIQUITY) * Math.sin(l));    // declination (lat 0)
  const ra = Math.atan2(Math.sin(l) * Math.cos(OBLIQUITY), Math.cos(l));
  const h = D2R * (280.16 + 360.9856235 * d) - lw - ra;        // hour angle
  const altitude = Math.asin(Math.sin(phi) * Math.sin(dec)
    + Math.cos(phi) * Math.cos(dec) * Math.cos(h));
  // SunCalc measures azimuth from due south, positive toward the west; shift by
  // 180° to a compass bearing from north.
  const azSouth = Math.atan2(Math.sin(h),
    Math.cos(h) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi));
  return {
    azimuth: (azSouth / R2D + 180 + 360) % 360,
    altitude: altitude * R2D,
  };
}

// --- solar terminator (the global day/night boundary) -----------------------
// Port of the standard Leaflet.Terminator maths: find the sun's equatorial
// position, then for each longitude solve the latitude at which the sun sits at
// a given depression angle. Fill the dark hemisphere as a polygon.

function julianDay(date: Date): number {
  return date.valueOf() / DAY_MS + 2_440_587.5;
}

function gmst(jd: number): number { // Greenwich mean sidereal time, hours
  return (18.697374558 + 24.06570982441908 * (jd - J2000)) % 24;
}

interface SunEq { ra: number; dec: number } // right ascension + declination, deg

function sunEquatorial(jd: number): SunEq {
  const n = jd - J2000;
  const L = (280.460 + 0.9856474 * n) % 360;             // mean longitude
  const g = ((357.528 + 0.9856003 * n) % 360) * D2R;     // mean anomaly
  const lambda = (L + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g)) * D2R;
  const eps = (23.439 - 0.0000004 * n) * D2R;             // obliquity
  return {
    ra: Math.atan2(Math.cos(eps) * Math.sin(lambda), Math.cos(lambda)) * R2D,
    dec: Math.asin(Math.sin(eps) * Math.sin(lambda)) * R2D,
  };
}

const clampLat = (lat: number): number => Math.max(-89.9, Math.min(89.9, lat));

// Latitude where the sun's altitude equals `depression` (0 = geometric night
// edge, -6 civil dusk, -18 astronomical night) at this longitude and time.
// Solves sin(alt) = sinφ·sinδ + cosφ·cosδ·cos(H) for φ.
function edgeLatitude(lng: number, sun: SunEq, gstHours: number,
                      depression: number): number {
  const ha = ((gstHours + lng / 15) * 15 - sun.ra) * D2R; // hour angle, rad
  const dec = sun.dec * D2R;
  const a = Math.sin(dec);
  const b = Math.cos(dec) * Math.cos(ha);
  const r = Math.hypot(a, b) || 1e-9;
  const s = Math.max(-1, Math.min(1, Math.sin(depression * D2R) / r));
  return clampLat((Math.asin(s) - Math.atan2(b, a)) * R2D);
}

// A polygon over the region darker than `depression`, as a GeoJSON feature.
function darkPolygon(sun: SunEq, gstHours: number, depression: number,
                     band: string): GeoJSON.Feature {
  const ring: [number, number][] = [];
  for (let lng = -180; lng <= 180; lng += 2) {
    ring.push([lng, edgeLatitude(lng, sun, gstHours, depression)]);
  }
  // Close over whichever pole is in darkness: the sun in the north (dec > 0)
  // leaves the south pole dark, and vice versa.
  const poleLat = sun.dec > 0 ? -89.9 : 89.9;
  ring.push([180, poleLat], [-180, poleLat], ring[0]!);
  return {
    type: 'Feature', properties: { band },
    geometry: { type: 'Polygon', coordinates: [ring] },
  };
}

function terminatorData(date: Date): GeoJSON.FeatureCollection {
  const jd = julianDay(date);
  const sun = sunEquatorial(jd);
  const g = gmst(jd);
  // Two stacked bands: a wide dusk wash to the geometric edge, and a darker
  // core past astronomical twilight. Their overlap grades the terminator.
  return {
    type: 'FeatureCollection',
    features: [
      darkPolygon(sun, g, 0, 'dusk'),
      darkPolygon(sun, g, -18, 'night'),
    ],
  };
}

// --- colour ramps keyed on the sun's altitude at the map centre --------------

type RGB = [number, number, number];

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const lerpRGB = (a: RGB, b: RGB, t: number): RGB =>
  [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
const rgb = ([r, g, b]: RGB): string =>
  `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;

interface SkyStop { alt: number; sky: RGB; horizon: RGB; fog: RGB }

const SKY_STOPS: SkyStop[] = [
  { alt: -18, sky: [8, 10, 22],    horizon: [14, 18, 38],    fog: [10, 12, 28] },
  { alt: -6,  sky: [22, 28, 56],   horizon: [58, 46, 74],    fog: [30, 30, 55] },
  { alt: 0,   sky: [74, 92, 148],  horizon: [226, 132, 72],  fog: [150, 120, 118] },
  { alt: 8,   sky: [92, 150, 214], horizon: [226, 180, 140], fog: [200, 190, 180] },
  { alt: 60,  sky: [74, 144, 217], horizon: [190, 214, 239], fog: [222, 228, 236] },
];

function sampleSky(alt: number): SkyStop {
  const s = SKY_STOPS;
  if (alt <= s[0]!.alt) return s[0]!;
  if (alt >= s[s.length - 1]!.alt) return s[s.length - 1]!;
  for (let i = 0; i < s.length - 1; i++) {
    const lo = s[i]!;
    const hi = s[i + 1]!;
    if (alt <= hi.alt) {
      const t = (alt - lo.alt) / (hi.alt - lo.alt);
      return {
        alt,
        sky: lerpRGB(lo.sky, hi.sky, t),
        horizon: lerpRGB(lo.horizon, hi.horizon, t),
        fog: lerpRGB(lo.fog, hi.fog, t),
      };
    }
  }
  return s[s.length - 1]!;
}

// Direct sunlight: warm and dim at the horizon, near-white overhead.
function sunlight(alt: number): RGB {
  const warm: RGB = [255, 176, 116];
  const white: RGB = [255, 250, 236];
  return lerpRGB(warm, white, Math.min(1, Math.max(0, alt / 12)));
}

// Shadows lengthen and soften as the sun drops, then fade out once it's down.
function hillshadeExaggeration(alt: number): number {
  if (alt >= 10) return 0.55;
  if (alt >= 0) return lerp(0.7, 0.55, alt / 10);
  return Math.max(0.15, 0.7 + (alt / 6) * 0.55);
}

// --- controller -------------------------------------------------------------

const HILLSHADE_ID = 'daynight-hillshade';
const TERMINATOR_SRC = 'daynight-terminator';
const DUSK_ID = 'daynight-dusk';
const NIGHT_ID = 'daynight-night';
// Insert everything just under the first overlay so markers, labels and events
// always stay on top (and bright); fall back to the top of the stack if none.
const OVERLAY_IDS = [
  'coverage-fill', 'entities-fill', 'entities-outline', 'entities-line',
  'entities-damage', 'entities-ring', 'entities-symbol', 'entities-label',
  'live-halo', 'live-dot', 'live-glyph', 'live-glyph-new', 'selected-buffer-fill',
];

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

export interface DayNight {
  isOn: () => boolean;
  toggle: () => boolean;
  setActive: (on: boolean) => void;
}

export function installDayNight(map: maplibregl.Map): DayNight {
  let active = false;
  let timer: number | null = null;
  let queued = false;

  function firstOverlayId(): string | undefined {
    for (const id of OVERLAY_IDS) if (map.getLayer(id)) return id;
    return undefined;
  }

  // The sun-lit relief embosses the whole map and reads as "3D terrain" even in
  // flat 2D, so it's tied to the actual 3D-terrain state: shown only when the
  // user has terrain on (the ⛰ control), hidden in 2D. The day/night terminator
  // and sky stand on their own and work in plain 2D.
  function syncRelief(): void {
    if (!map.getLayer(HILLSHADE_ID)) return;
    map.setLayoutProperty(HILLSHADE_ID, 'visibility',
      map.getTerrain() ? 'visible' : 'none');
    // A relief that just became visible needs the current sun on it now, not
    // at the next minute tick or the next big pan.
    lastSun = null;
    applyLighting();
  }

  function ensureLayers(): void {
    const before = firstOverlayId();
    installTerrain(map); // idempotent — guarantees the DEM source exists

    if (!map.getLayer(HILLSHADE_ID)) {
      map.addLayer({
        id: HILLSHADE_ID, type: 'hillshade', source: TERRAIN_SOURCE_ID,
        layout: { visibility: map.getTerrain() ? 'visible' : 'none' },
        paint: { 'hillshade-illumination-anchor': 'map' },
      }, before);
    }
    if (!map.getSource(TERMINATOR_SRC)) {
      map.addSource(TERMINATOR_SRC, { type: 'geojson', data: EMPTY });
    }
    // Dusk (wide) below, night (core) above — the overlap grades the edge.
    if (!map.getLayer(DUSK_ID)) {
      map.addLayer({
        id: DUSK_ID, type: 'fill', source: TERMINATOR_SRC,
        filter: ['==', ['get', 'band'], 'dusk'],
        paint: { 'fill-color': '#0e1636', 'fill-opacity': 0.30, 'fill-antialias': false },
      }, before);
    }
    if (!map.getLayer(NIGHT_ID)) {
      map.addLayer({
        id: NIGHT_ID, type: 'fill', source: TERMINATOR_SRC,
        filter: ['==', ['get', 'band'], 'night'],
        paint: { 'fill-color': '#05060f', 'fill-opacity': 0.42, 'fill-antialias': false },
      }, before);
    }
  }

  function removeLayers(): void {
    for (const id of [NIGHT_ID, DUSK_ID, HILLSHADE_ID]) {
      if (map.getLayer(id)) map.removeLayer(id);
    }
    if (map.getSource(TERMINATOR_SRC)) map.removeSource(TERMINATOR_SRC);
  }

  // The terminator is a function of TIME alone, so it is rebuilt on the minute
  // timer and never on a pan: setData re-tiles the polygon in the worker, and
  // this used to run on every `move` frame.
  function applyTerminator(): void {
    // Guard on the layers existing, NOT on isStyleLoaded(): adding the
    // hillshade's raster-DEM source flips isStyleLoaded() to false while its
    // tiles stream in, and gating on that would skip the one apply() that runs
    // right after the layers are built — leaving the terminator source empty.
    const src = map.getSource(TERMINATOR_SRC) as maplibregl.GeoJSONSource | undefined;
    if (!active || !src) return;
    src.setData(terminatorData(new Date()));
  }

  // The sky, light and relief follow the sun at the map CENTRE, so they do
  // move with a pan — but each setter marks the style dirty and re-renders,
  // so they are only touched once the sun has visibly moved (a fraction of a
  // degree; a pan across a city changes nothing, across a country a little).
  let lastSun: SunPos | null = null;
  function applyLighting(): void {
    if (!active || !map.getSource(TERMINATOR_SRC)) return;
    const now = new Date();
    const c = map.getCenter();
    const sun = sunPosition(now, c.lat, c.lng);
    if (lastSun) {
      const dAz = Math.abs(((sun.azimuth - lastSun.azimuth + 540) % 360) - 180);
      if (Math.abs(sun.altitude - lastSun.altitude) < 0.2 && dAz < 0.5) return;
    }
    lastSun = sun;
    const sky = sampleSky(sun.altitude);

    map.setSky({
      'sky-color': rgb(sky.sky),
      'horizon-color': rgb(sky.horizon),
      'fog-color': rgb(sky.fog),
      'sky-horizon-blend': 0.6,
      'horizon-fog-blend': 0.6,
      'fog-ground-blend': 0.5,
      'atmosphere-blend': 0.8,
    });

    // Light the (rare) extruded geometry from the sun too; harmless otherwise.
    const polar = Math.min(179, Math.max(1, 90 - Math.max(sun.altitude, -2)));
    map.setLight({
      anchor: 'map',
      position: [1.15, sun.azimuth, polar],
      color: rgb(sunlight(sun.altitude)),
      intensity: sun.altitude > 0 ? 0.5 : 0.15,
    });

    if (map.getLayer(HILLSHADE_ID) && map.getTerrain()) {
      const lit = sunlight(sun.altitude);
      map.setPaintProperty(HILLSHADE_ID, 'hillshade-illumination-direction',
        Math.round(sun.azimuth));
      map.setPaintProperty(HILLSHADE_ID, 'hillshade-exaggeration',
        hillshadeExaggeration(sun.altitude));
      map.setPaintProperty(HILLSHADE_ID, 'hillshade-highlight-color', rgb(lit));
      map.setPaintProperty(HILLSHADE_ID, 'hillshade-shadow-color',
        rgb(lerpRGB([12, 16, 34], [40, 34, 30], Math.min(1, Math.max(0, sun.altitude / 30)))));
    }
  }

  /** Everything, unconditionally — the minute tick and the toggles. */
  function apply(): void {
    applyTerminator();
    lastSun = null;
    applyLighting();
  }

  // The terminator is global, so panning doesn't change it — only the centre-
  // based sky/relief needs the move. Coalesce the flood into one update/frame.
  function scheduleApply(): void {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; applyLighting(); });
  }

  function start(): void {
    const go = () => {
      if (!active) return; // toggled back off before the style was ready
      ensureLayers();
      map.on('move', scheduleApply);
      map.on('terrain', syncRelief); // show/hide the relief as 3D is toggled
      timer = window.setInterval(apply, 60_000); // the terminator keeps moving
      apply();
    };
    if (map.isStyleLoaded()) go();
    else map.once('load', go);
  }

  function stop(): void {
    map.off('move', scheduleApply);
    map.off('terrain', syncRelief);
    if (timer !== null) { clearInterval(timer); timer = null; }
    removeLayers();
    // Called with no argument, both reset to the style default at runtime; the
    // typings mark the argument required, so loosen the signature to omit it.
    (map.setSky as (sky?: unknown) => void)();
    (map.setLight as (light?: unknown) => void)();
  }

  function setActive(on: boolean): void {
    if (on === active) return;
    active = on;
    if (on) start(); else stop();
  }

  return {
    isOn: () => active,
    toggle: () => { setActive(!active); return active; },
    setActive,
  };
}
